import { Injectable } from '@angular/core';
import {
  Client,
  createInstance,
  createPollingProjectConfigManager,
} from '@optimizely/optimizely-sdk';
import { defer, distinctUntilKeyChanged, from, Observable, of } from 'rxjs';
import { catchError, map, retry, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AccountService } from './account.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private readonly optimizelyClient: Client;

  public constructor(private readonly accountService: AccountService) {
    this.optimizelyClient = createInstance({
      projectConfigManager: createPollingProjectConfigManager({
        sdkKey: environment.optimizelySdkKey,
      }),
    });
  }

  public isFeatureEnabled(featureKey: string): Observable<boolean> {
    // defer so each retry attempt calls onReady() afresh — the promise captured by a
    // single from() would only ever replay its original rejection. A fresh onReady()
    // re-checks the datafile the SDK's polling manager reloads in the background after a
    // transient CDN/network failure (see EMMET-B).
    return defer(() => from(this.optimizelyClient.onReady())).pipe(
      retry({ count: 3, delay: 5000 }),
      switchMap(() => this.accountService.getActiveAccount()),
      distinctUntilKeyChanged('homeAccountId'),
      map((account) =>
        this.optimizelyClient.isFeatureEnabled(featureKey, account.homeAccountId, {
          email: account.username,
        })
      ),
      // A feature flag that cannot be resolved degrades to "off" rather than escalating
      // to an unhandled error that crashes the app.
      catchError(() => of(false))
    );
  }
}
